import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Chip,
    alpha,
    useTheme,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BugReportIcon from "@mui/icons-material/BugReport";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion, AnimatePresence } from "framer-motion";

const BUG_AREAS = [
    { value: "gameplay", label: "Gameplay" },
    { value: "timer", label: "Timer" },
    { value: "chat", label: "Chat" },
    { value: "bots", label: "Bots" },
    { value: "ui", label: "UI / Display" },
    { value: "connection", label: "Connection" },
    { value: "other", label: "Other" },
];

const FEEDBACK_AREAS = [
    { value: "gameplay", label: "Gameplay" },
    { value: "bots", label: "Bot Behaviour" },
    { value: "ui", label: "UI / Design" },
    { value: "performance", label: "Performance" },
    { value: "feature-request", label: "Feature Request" },
    { value: "other", label: "Other" },
];

const SEVERITIES = [
    { value: "low", label: "Low", color: "#4CAF50" },
    { value: "medium", label: "Medium", color: "#FF9800" },
    { value: "high", label: "High", color: "#f44336" },
    { value: "critical", label: "Critical 🔥", color: "#b71c1c" },
];

const SERVER_URL = process.env.REACT_APP_SERVER_URL || "http://localhost:4000";

function FeedbackDialog({ open, onClose, roomCode }) {
    const theme = useTheme();
    const [category, setCategory] = useState(""); // "bug" | "feedback"
    const [bugArea, setBugArea] = useState("");
    const [severity, setSeverity] = useState("");
    const [feedbackArea, setFeedbackArea] = useState("");
    const [message, setMessage] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const reset = () => {
        setCategory("");
        setBugArea("");
        setSeverity("");
        setFeedbackArea("");
        setMessage("");
        setEmail("");
        setError("");
        setSubmitted(false);
        setLoading(false);
    };

    const handleClose = () => {
        onClose();
        setTimeout(reset, 300); // reset after close animation
    };

    const canSubmit =
        category &&
        message.trim().length >= 5 &&
        (category === "bug" ? bugArea && severity : feedbackArea);

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setLoading(true);
        setError("");
        try {
            const res = await fetch(`${SERVER_URL}/api/feedback`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    category,
                    bugArea: category === "bug" ? bugArea : "",
                    severity: category === "bug" ? severity : "",
                    feedbackArea: category === "feedback" ? feedbackArea : "",
                    message: message.trim(),
                    email: email.trim(),
                    roomCode: roomCode || "",
                }),
            });
            if (!res.ok) throw new Error("Server error");
            setSubmitted(true);
        } catch (e) {
            setError("Failed to submit. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const glass = {
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: "blur(16px)",
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    ...glass,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 3,
                    boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
                },
            }}
        >
            <DialogTitle sx={{ pb: 1, pr: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
                    {submitted ? "Thank you! 🙏" : "Report a Bug / Give Feedback"}
                </Typography>
                {!submitted && (
                    <Typography variant="body2" sx={{ opacity: 0.5, mt: 0.25, fontSize: "0.78rem" }}>
                        Help us make RMCS better
                    </Typography>
                )}
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{ position: "absolute", right: 12, top: 12, opacity: 0.6 }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                <AnimatePresence mode="wait">
                    {submitted ? (
                        <motion.div
                            key="success"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <Box sx={{ textAlign: "center", py: 4 }}>
                                <CheckCircleIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    Feedback received!
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.6, maxWidth: 320, mx: "auto" }}>
                                    We read every submission. Thanks for taking the time — it genuinely helps.
                                </Typography>
                            </Box>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="form"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>

                                {/* Step 1: Category */}
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 1, mb: 1, display: "block" }}>
                                        What is this about?
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1.5 }}>
                                        {[
                                            { value: "bug", label: "Bug Report", icon: <BugReportIcon sx={{ fontSize: 18 }} />, color: theme.palette.error.main },
                                            { value: "feedback", label: "Suggestion", icon: <LightbulbIcon sx={{ fontSize: 18 }} />, color: theme.palette.warning.main },
                                        ].map((opt) => (
                                            <Box
                                                key={opt.value}
                                                onClick={() => { setCategory(opt.value); setBugArea(""); setSeverity(""); setFeedbackArea(""); }}
                                                sx={{
                                                    flex: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: 1,
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    border: `2px solid ${category === opt.value ? opt.color : theme.palette.divider}`,
                                                    backgroundColor: category === opt.value ? alpha(opt.color, 0.1) : "transparent",
                                                    color: category === opt.value ? opt.color : theme.palette.text.secondary,
                                                    cursor: "pointer",
                                                    transition: "all 0.2s ease",
                                                    fontWeight: 700,
                                                    fontSize: "0.85rem",
                                                    "&:hover": {
                                                        borderColor: opt.color,
                                                        backgroundColor: alpha(opt.color, 0.06),
                                                    },
                                                }}
                                            >
                                                {opt.icon}
                                                {opt.label}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>

                                {/* Step 2: Category-specific fields */}
                                <AnimatePresence mode="wait">
                                    {category === "bug" && (
                                        <motion.div
                                            key="bug-fields"
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -8 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                {/* Bug area chips */}
                                                <Box>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 1, mb: 1, display: "block" }}>
                                                        Where is the bug?
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                                        {BUG_AREAS.map((a) => (
                                                            <Chip
                                                                key={a.value}
                                                                label={a.label}
                                                                size="small"
                                                                onClick={() => setBugArea(a.value)}
                                                                variant={bugArea === a.value ? "filled" : "outlined"}
                                                                color={bugArea === a.value ? "error" : "default"}
                                                                sx={{ cursor: "pointer", fontWeight: 600, fontSize: "0.75rem" }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>

                                                {/* Severity */}
                                                <Box>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 1, mb: 1, display: "block" }}>
                                                        How bad is it?
                                                    </Typography>
                                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                        {SEVERITIES.map((s) => (
                                                            <Box
                                                                key={s.value}
                                                                onClick={() => setSeverity(s.value)}
                                                                sx={{
                                                                    px: 1.5, py: 0.5,
                                                                    borderRadius: 1.5,
                                                                    border: `1.5px solid ${severity === s.value ? s.color : theme.palette.divider}`,
                                                                    backgroundColor: severity === s.value ? alpha(s.color, 0.12) : "transparent",
                                                                    color: severity === s.value ? s.color : theme.palette.text.secondary,
                                                                    cursor: "pointer",
                                                                    fontSize: "0.78rem",
                                                                    fontWeight: 700,
                                                                    transition: "all 0.15s ease",
                                                                }}
                                                            >
                                                                {s.label}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </motion.div>
                                    )}

                                    {category === "feedback" && (
                                        <motion.div
                                            key="feedback-fields"
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -8 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <Box>
                                                <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 1, mb: 1, display: "block" }}>
                                                    Which area?
                                                </Typography>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                                    {FEEDBACK_AREAS.map((a) => (
                                                        <Chip
                                                            key={a.value}
                                                            label={a.label}
                                                            size="small"
                                                            onClick={() => setFeedbackArea(a.value)}
                                                            variant={feedbackArea === a.value ? "filled" : "outlined"}
                                                            color={feedbackArea === a.value ? "warning" : "default"}
                                                            sx={{ cursor: "pointer", fontWeight: 600, fontSize: "0.75rem" }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Message — always shown but prettier once category is chosen */}
                                {category && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2, delay: 0.05 }}
                                    >
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            label={category === "bug" ? "Describe the bug" : "Your suggestion"}
                                            placeholder={
                                                category === "bug"
                                                    ? "What happened? What did you expect? Steps to reproduce (optional)..."
                                                    : "What would make RMCS better for you?..."
                                            }
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            inputProps={{ maxLength: 2000 }}
                                            helperText={`${message.length}/2000`}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    fontSize: "0.875rem",
                                                },
                                            }}
                                        />
                                    </motion.div>
                                )}

                                {/* Email — optional */}
                                {category && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2, delay: 0.1 }}
                                    >
                                        <TextField
                                            fullWidth
                                            label="Your email (optional)"
                                            placeholder="We'll only reach out if we need more info"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            inputProps={{ maxLength: 200 }}
                                            size="small"
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.875rem" } }}
                                        />
                                    </motion.div>
                                )}

                                {/* Room code context — show if in a game */}
                                {roomCode && (
                                    <Typography variant="caption" sx={{ opacity: 0.35, fontSize: "0.7rem" }}>
                                        Room code <strong>{roomCode}</strong> will be attached automatically.
                                    </Typography>
                                )}

                                {error && (
                                    <Typography variant="body2" sx={{ color: "error.main", fontSize: "0.8rem" }}>
                                        {error}
                                    </Typography>
                                )}
                            </Box>
                        </motion.div>
                    )}
                </AnimatePresence>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                {submitted ? (
                    <Button variant="contained" onClick={handleClose} fullWidth sx={{ borderRadius: 2, fontWeight: 700 }}>
                        Close
                    </Button>
                ) : (
                    <>
                        <Button onClick={handleClose} variant="text" sx={{ color: theme.palette.text.secondary }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={!canSubmit || loading}
                            sx={{
                                borderRadius: 2,
                                fontWeight: 700,
                                minWidth: 120,
                                background: category === "bug"
                                    ? `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`
                                    : `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                            }}
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {loading ? "Sending..." : "Submit"}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default FeedbackDialog;